import { useBreakpoint } from '@/shared/lib/useBreakpoint';
import { typography, radius } from '@/shared/theme/tokens';
import { SkeletonCard } from '@/shared/ui/SkeletonCard';
import { ErrorState } from '@/shared/ui/ErrorState';
import { useLiveModeModel } from '../../hooks/useLiveModeModel';
import { BCAST, KEYFRAMES } from './broadcastMeta';
import { BreakingBar } from './BreakingBar';
import { LiveBug } from './LiveBug';
import { LowerThird } from './LowerThird';
import { UpNextList } from './UpNextList';
import { StudioBackdrop } from './StudioBackdrop';
import { TickerRibbon } from './TickerRibbon';

/**
 * Broadcast / News-channel variant — a 24/7 financial-news control-room display.
 *
 * Channel chrome over the SHARED Live Mode model (identical data + IA to every
 * other variant; only the visual language differs):
 *  - a state-coloured breaking-news headline bar (drives off `headline`)
 *  - a persistent corner "LIVE" bug with the clock + data freshness
 *  - an anchor lower-third for the top recommendation, the rest as "UP NEXT"
 *  - the furnace as a small studio backdrop (NOT the hero)
 *  - a single scrolling alert ticker along the bottom
 *
 * Read-only: cards/segments open the page-owned drawer via onSelectRec/onSelectAlert.
 */
export function BroadcastScreen({ deploymentName, svgUrl, seedAlerts, seedRecs, isLoading, isError, onSelectRec, onSelectAlert }) {
  const { isDesktop } = useBreakpoint();
  const {
    alerts, recs, clock, flashId, jitterActive,
    degraded, rankedRecs, rankedAlerts,
    headline, freshness, animate,
  } = useLiveModeModel(seedAlerts, seedRecs);

  const showSkeleton = isLoading && alerts.length === 0 && recs.length === 0;

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0,
      background: BCAST.studio, color: BCAST.ink, fontFamily: typography.fontFamily.body,
    }}>
      <style>{KEYFRAMES}</style>

      <BreakingBar headline={headline} animate={animate} />

      {isError ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: BCAST.panel }}>
          <ErrorState message="OFF AIR — couldn't load the live feed for this deployment." />
        </div>
      ) : showSkeleton ? (
        <div style={{
          flex: 1, minHeight: 0, display: isDesktop ? 'grid' : 'flex', flexDirection: isDesktop ? undefined : 'column',
          gridTemplateColumns: isDesktop ? '1.7fr 1fr' : undefined, gap: 14, padding: 14, background: BCAST.studio,
        }}>
          <div style={{ background: BCAST.panel, borderRadius: radius.sm, padding: 18 }}><SkeletonCard rows={6} height="100%" /></div>
          <div style={{ background: BCAST.panel, borderRadius: radius.sm, padding: 18 }}><SkeletonCard rows={6} height="100%" /></div>
        </div>
      ) : (
        <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Persistent LIVE bug — pinned to the studio corner, above the set */}
          <div style={{ position: 'absolute', top: 14, right: 16, zIndex: 5 }}>
            <LiveBug
              deploymentName={deploymentName}
              clock={clock}
              freshness={freshness}
              jitterActive={jitterActive}
              animate={animate}
            />
          </div>

          <div style={{
            flex: 1, minHeight: 0,
            display: isDesktop ? 'grid' : 'flex', flexDirection: isDesktop ? undefined : 'column',
            gridTemplateColumns: isDesktop ? '1.7fr 1fr' : undefined,
            gap: 14, padding: 14, overflow: isDesktop ? 'hidden' : 'auto',
          }}>
            {/* Anchor column: lower-third + up-next queue */}
            <div style={{ minWidth: 0, minHeight: isDesktop ? 0 : 280, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
                <LowerThird rec={rankedRecs[0]} degraded={degraded} animate={animate} onSelect={onSelectRec} />
              </div>
              <UpNextList recs={rankedRecs} onSelect={onSelectRec} />
            </div>

            {/* Studio backdrop column */}
            <div style={{ minWidth: 0, minHeight: isDesktop ? 0 : 240, display: 'flex' }}>
              <StudioBackdrop svgUrl={svgUrl} params={rankedAlerts} />
            </div>
          </div>
        </div>
      )}

      <TickerRibbon alerts={rankedAlerts} flashId={flashId} animate={animate} onSelect={onSelectAlert} />
    </div>
  );
}
